import DebugService from './DebugService';
import I18nService from './I18nService';
import Api, {API_STATUS} from './API';
import config from '../configs';

// store
import store from '../redux/store';
import {generateGlobalKey} from '../redux/_AppStore';

/**
 * Service
 * @folder Services
 * @class
 */
class Service {

  /**
   * Renvoie l'instance du service
   * @returns {Service} singleton instance
   */
  static getInstance() {
    if (!Service._instance) {
      Service._instance = new Service(store);
      if (DebugService.getInstance().enabled) console.log('Service - instanciated.',{usecache:process.env.REACT_APP_USE_CACHE});
    }
    return Service._instance;
  }

  constructor(storeArg) {
    this.api = new Api();
    this.DEBUG = DebugService.getInstance().enabled;
    this.USE_CACHE = !!process.env.REACT_APP_USE_CACHE && process.env.REACT_APP_USE_CACHE === 'true';
    this.store = storeArg;

    this.token = undefined; // token passé au back lors des requetes
    this.enabled = false; // par defaut le chatbot n'est pas active
    this.ui_params = undefined; // infos transmises via le endpoint hello
    this.configured = false; // true si on a recu un hello OK
    this.querying = false;  // true si une requete query est en cours
    this.checkingAnswer = false;  // true si une ou des requetes checkAnswer sont en cours
    this.checkingAnswerIterations = 0;
    this.checkingAnswerIterations500 = 0;
    this.rating = false;

    this.session_id = undefined;  // {string} ID lié à toute la conversation à partir du moment où elle est engagée (uuid)
    this.process_id = undefined; // {string} ID lié à une query en cours (timestamp)
    this.retry_after = undefined; // {number} en secondes, recupere de la reponse du query, pour les cas 500
    // this.messages_history_id = undefined; // {array of string} response_id des messages qui ont deja ete trigger (timestamp)

    if (this.USE_CACHE) {
      this.session_id = localStorage.getItem(config.CACHE_VAR+'_session_id');
      if (this.session_id === null) this.session_id = undefined;
      if (this.DEBUG) console.log('Service - session_id from localStorage',{session_id:this.session_id});

      this.process_id = localStorage.getItem(config.CACHE_VAR+'_process_id');
      if (this.process_id === null) this.process_id = undefined;
      if (this.DEBUG) console.log('Service - process_id from localStorage',{process_id:this.process_id});

      // let messages_history_id_cache = localStorage.getItem(config.CACHE_VAR+'_messages_history_id');
      // if (messages_history_id_cache === null) messages_history_id_cache = undefined;
      // this.messages_history_id = !!messages_history_id_cache ? JSON.parse(messages_history_id_cache) : undefined;
      // if (this.DEBUG) console.log('Service - messages_history_id from localStorage',{messages_history_id:this.messages_history_id});
    }
  }

  /**
   * Store the site token, needed for every API requests
   * @param {string} token
   */
  setToken(token) {
    this.token = token;
  }

  /**
   *
   */
  generateGlobalKey() {
    if (this.DEBUG) console.log('Service - generateGlobalKey...');
    this.store.dispatch(generateGlobalKey());
  }

  /**
   * Request that check if the API is UP, if the token is valid. Returns configuration data :
   * * message = "Hello" is all is good
   * * message = "XXX" is something goes wrong, explicit label
   * * i18n labels + 2digits locale + other options
   * @param {function} cb callback, take boolean as param, true if API is up, false otherwise
   */
  callAPIHello(cb) {
    if (this.DEBUG) console.log('Service - callAPIHello...');

    // check token
    if (!this.token) {
      if (this.DEBUG) console.log('Service - callAPIHello : Missing token, canot request the API, abort.');
      if (cb) cb(false);
      return;
    }

    this.api.getHello(this.token)
      .then((res) => {
        if (!res.ok) {
          if (cb) cb(false);
          return;
        }
        return res;
      })
      .then(async (res) => ({
        status: res.status,
        body: await res.json()
      }))
      .then(({ status, body }) => {
        if (this.DEBUG) console.log('Service - callAPIHello, response is',{status,body});
        if (body.message === 'Hello') {
          this.ui_params = body.ui_params;
          this.configured = true;
        }
        if (cb) cb(body.message === 'Hello');
      }).catch(error => {
        if (this.DEBUG) console.log('Service - callAPIHello, cannot get the response',error);
        if (cb) cb(false);
      });
  }

  /**
   *
   * @param {string} uservalue
   * @returns {Promise} si error, renvoie le message {string}. si ok, renvoie un {object} avec le body de la reponse
   */
  runQuery(uservalue) {
    if (this.DEBUG) console.log('Service - runQuery...');

    // check token
    if (!this.token) {
      if (this.DEBUG) console.log('Service - runQuery : Missing token, canot request the API, abort.');
      return new Promise((resolve) => {
        resolve({
          status: "error",
          message: "Missing token"
        });
      });
    }

    // check session_id
    // le session_id est créé par l'API si c'est la 1ere query, donc undfined
    // sinon il a ete stocké dans this.session_id

    this.querying = true;

    const bodycontents = {
      query: uservalue,
      src_page: window.location.href    // pour des besoins de stats
    };

    return new Promise((resolve) => {
      if (this.DEBUG) console.log('Service - runQuery : api.postQuery...',{token:this.token,session_id:this.session_id,bodycontents});
      this.api.postQuery(this.token, this.session_id, bodycontents)
        .then(async (res) => ({
          status: res.status,
          body: res.ok ? await res.json() : {}
        }))
        .then(({ status, body }) => {
          if (this.DEBUG) console.log('Service - runQuery, response is',{status,body});
          this.querying = false;
          if (status === 200 || status === 202) {
            if (this.DEBUG && status === 200) console.log('Service - runQuery, seems good',body);
            if (this.DEBUG && status === 202) console.log('Service - runQuery, seems good but processing',body);

            // si c la 1ere query, ou si le session_id a changé, il faut stocker le session_id
            if (this.session_id === undefined || this.session_id !== body.session_id) {
              this.session_id = body.session_id;
              if (this.USE_CACHE) localStorage.setItem(config.CACHE_VAR+'_session_id',body.session_id);
              if (this.DEBUG) console.log('Service - runQuery, store session_id',{session_id:body.session_id, USE_CACHE:this.USE_CACHE});
            }
            // si c la 1ere query, ou si le process_id a changé, il faut stocker le process_id
            if (this.process_id === undefined || this.process_id !== body.process_id) {
              this.process_id = body.process_id;
              if (this.USE_CACHE) localStorage.setItem(config.CACHE_VAR+'_process_id',body.process_id);
              if (this.DEBUG) console.log('Service - runQuery, store process_id',{process_id:body.process_id, USE_CACHE:this.USE_CACHE});
            }
            // si c la 1ere query, ou si le process_id a changé, il faut stocker le retry_after
            if (this.retry_after === undefined || this.retry_after !== body.retry_after) {
              this.retry_after = body.retry_after;
            }
            resolve(body);
          }
          else if (status === 409) {  // conflict
            if (this.DEBUG) console.log('Service - runQuery, in conflict',body);

            if (this.process_id !== undefined) {
              if (this.DEBUG) console.log('Service - runQuery, conflict, run check_answer with store process_id...');
              // on simule une reponse correcte de query
              const fake200Body = {
                status: API_STATUS.PROCESSING,
                session_id: this.session_id,
                process_id: this.process_id,
                retry_after: 2, // 2 sec arbitraire
              };
              resolve(fake200Body);
            }
            else {
              if (this.DEBUG) console.log('Service - runQuery, conflict, no store process_id, reset session...');
              this.resetSession();
              resolve({
                status: "error",
                message: 'Last request lost. New session started. Please ask your request again.'
              });
            }
          }
          else {
            if (this.DEBUG) console.log('Service - runQuery, unknwon status',body);
            resolve({
              status: "error",
              message: body.message
            });
          }
        }).catch(error => {
          if (this.DEBUG) console.log('Service - runQuery, cannot get the response',error);
          this.querying = false;
          resolve({
            status: "error",
            message: error
          });
        });
    });

  }

  /**
   * Réalise une ou plusieurs requêtes check-answer
   * @param {object} queryResponse
   * @param {number} newDelay force new delay in milliseconds
   * @returns {Promise} si error, renvoie le message {string}. si ok, renvoie un {object} avec le body de la reponse
   */
  checkAnswer(queryResponse, newDelay = undefined) {
    // on relit les derniers agents ids affichés
    let lastAgentsIds = undefined;
    if (this.USE_CACHE) {
      try{
        lastAgentsIds = JSON.parse(localStorage.getItem(config.CACHE_VAR+'_last_agent_ids'));
      } catch(err) {
        console.warn('Service - Cannot parse ',config.CACHE_VAR+'_last_agent_ids', 'in localStorage');
      }
    }

    if (this.DEBUG) console.log('Service - checkAnswer...',{queryResponse,checkingAnswerIterations:this.checkingAnswerIterations,lastAgentsIds});

    // si on a itéré le nb max d'appels check_answer, on s'arrete
    let max_call_check_answer = config.MAX_CALL_CHECK_ANSWER;
    if (this.ui_params && this.ui_params.max_call_check_answer !== undefined && Number.isInteger(this.ui_params.max_call_check_answer)) {
      max_call_check_answer = this.ui_params.max_call_check_answer;
    }
    if (this.checkingAnswerIterations >= max_call_check_answer) {
      return new Promise((resolve) => {
        if (this.DEBUG) console.log('Service - checkAnswer, reached the max check_answer calls number',max_call_check_answer);
        this.checkingAnswer = false;
        this.checkingAnswerIterations = 0;
        this.checkingAnswerIterations500 = 0;
        resolve({
          status: "error",
          message: "Max calls reached, abort"
        });
      });
    }

    // si on a eu 3 essais en 500, on arrete
    if (this.checkingAnswerIterations500 >= 3) {
      return new Promise((resolve) => {
        if (this.DEBUG) console.log('Service - checkAnswer, reached the max 500 calls number (3)');
        this.checkingAnswer = false;
        this.checkingAnswerIterations = 0;
        this.checkingAnswerIterations500 = 0;
        const errorMessage = I18nService.getInstance().t('label.errorMessage');
        resolve({
          status: "error",
          message: errorMessage
        });
      });
    }

    this.checkingAnswer = true;
    this.checkingAnswerIterations++;
    const session_id = queryResponse.session_id;
    const process_id = queryResponse.process_id;

    return new Promise((resolve) => {
      if (this.DEBUG) console.log('Service - checkAnswer',{queryResponse});

      const thisFuckingThing = this;
      let retry_after = queryResponse.retry_after;
      if (retry_after === undefined || retry_after === null) retry_after = 'None';
      const DELAY = retry_after === 'None' ? 0 : parseInt(retry_after) * 1000;
      if (this.DEBUG) console.log('Service - checkAnswer - try after delay',{DELAY});
      setTimeout(() => {
        if (thisFuckingThing.DEBUG) console.log('Service - checkAnswer : api.getCheckAnswer...',{token:this.token,session_id,process_id});
        thisFuckingThing._doOneCheckAnswer(queryResponse)
          .then((response) => {
            // response est un object contenant le body de la reponse, ou bien {status:'error',message:'...'}
            thisFuckingThing.lastAnswerResponse = response;
            const lastStatus = thisFuckingThing.lastAnswerResponse.status;
            const lastMessage_history = this.lastAnswerResponse.message_history;
            const lastMessage = lastMessage_history && lastMessage_history.length > 0 ? lastMessage_history[lastMessage_history.length -1] : undefined;
            if (thisFuckingThing.DEBUG) console.log('Service - checkAnswer response of API',{lastStatus,lastMessage,response});

            const last_message_history_agents = lastMessage?.agent;

            const isDelayed = lastStatus === API_STATUS.PROCESSING && (last_message_history_agents === undefined || last_message_history_agents.length === 0);
            const isPartial = lastStatus === API_STATUS.PROCESSING && last_message_history_agents !== undefined && last_message_history_agents.length > 0;
            let isPartialChanged = false;
            if (isPartial && last_message_history_agents) {
              if (thisFuckingThing.DEBUG) console.log('Service - compare',{last_message_history_agents,lastAgentsIds});
              if (lastAgentsIds && lastAgentsIds.length > 0) {
                last_message_history_agents.forEach((agent) => {
                  const isNewAgent = lastAgentsIds.indexOf(agent.response_id) === -1;
                  if (isNewAgent) isPartialChanged = true;
                });
              } else {
                isPartialChanged = true;
              }
            }
            const is500 = (response.status === 'error' && response.message === 500) || response["500"] === true;
            if (is500) {
              this.checkingAnswerIterations500++;
            }
            if (thisFuckingThing.DEBUG) console.log('Service - special case?',{isDelayed,isPartial,isPartialChanged,is500});


            // ancien status "delayed" : aucune reponse, on reitere
            // nouveau status "processing" + sans agent dans le lastMessage : aucune reponse, on reitere
            // nouveau status "processing" + avec agent dans le lastMessage : reponse partielle, on renvoie les instructions, le composant relancera un check_answer
            // nouveau status "processing" + avec agent dans le lastMessage + pas de changement : on reitere
            // error 500 : on retry
            const needRetry = lastStatus === API_STATUS.DELAYED ||
                                isDelayed ||
                                (isPartial && !isPartialChanged) ||
                                is500;
            if (thisFuckingThing.DEBUG) console.log('Service - checkAnswer response of API',{needRetry});

            // pas encore de reponse
            if (needRetry) {
              // on recommence l'écoute avec les nouveaux delais

              let new_retry_after = thisFuckingThing.lastAnswerResponse.retry_after;
              if (new_retry_after === undefined || new_retry_after === null) new_retry_after = 'None';
              if (is500) new_retry_after = 1;  // 500 = retry apres 1sec
              const NEW_DELAY = new_retry_after === 'None' ? 0 : parseInt(new_retry_after) * 1000;

              // alerte cas pas normal
              if (DELAY === 0) {
                if (thisFuckingThing.DEBUG) console.warn('Service - checkAnswer response indicate processing answer, but query indicate no delay...');
              } else if (DELAY !== NEW_DELAY) {
                if (thisFuckingThing.DEBUG) console.warn('Service - checkAnswer response indicate processing answer of delay',NEW_DELAY,' but query indicate processing of delay',DELAY);
              }

              if (thisFuckingThing.DEBUG) console.log('Service - checkAnswer response processing',{lastStatus,response,NEW_DELAY});
              resolve(thisFuckingThing.checkAnswer(queryResponse,NEW_DELAY));
            }
            // reponse complete ou partielle
            else {
              // il faut stocker le message_history
              // if (thisFuckingThing.lastAnswerResponse.message_history) {
              //   if (this.messages_history_id === undefined) {
              //     this.messages_history_id = [];
              //   }
              //   thisFuckingThing.lastAnswerResponse.message_history.forEach(message => {
              //     if (!!message.response_id && this.messages_history_id.indexOf(message.response_id) === -1) {
              //       this.messages_history_id.push(message.response_id);
              //     }
              //   });
              // }
              // if (this.USE_CACHE) localStorage.setItem(config.CACHE_VAR+'_messages_history_id',JSON.stringify(this.messages_history_id));
              // if (this.DEBUG) console.log('Service - checkAnswer, store messages_history_id',{messages_history_id:this.messages_history_id, USE_CACHE:this.USE_CACHE});

              this.checkingAnswer = false;
              this.checkingAnswerIterations = 0;
              this.checkingAnswerIterations500 = 0;
              if (thisFuckingThing.DEBUG) console.log('Service - checkAnswer response ok, full or partial',{lastStatus,response});
              resolve(response);
            }
          })
          .catch(error => {
            if (this.DEBUG) console.log('Service - checkAnswer, cannot get the response',error);
            this.checkingAnswer = false;
            this.checkingAnswerIterations = 0;
            this.checkingAnswerIterations500 = 0;
            resolve({
              status: "error",
              message: error
            });
          });
      },newDelay !== undefined ? newDelay : DELAY);
    });
  }

  /**
   * Réalise une requête check_answer
   * @param {object} queryResponse
   * @returns {Promise} si error, renvoie le message {string}. si ok, renvoie un {object} avec le body de la reponse
   */
  _doOneCheckAnswer(queryResponse) {
    // check token
    if (!this.token) {
      if (this.DEBUG) console.log('Service - _doOneCheckAnswer : Missing token, canot request the API, abort.');
      return new Promise((resolve) => {
        resolve({
          status: "error",
          message: "Missing token"
        });
      });
    }

    // check session_id
    const session_id = queryResponse.session_id;
    if (!session_id) {
      if (this.DEBUG) console.log('Service - _doOneCheckAnswer : Missing session_id, canot request the API, abort.');
      return new Promise((resolve) => {
        resolve({
          status: "error",
          message: "Missing session_id"
        });
      });
    }

    // check process_id
    const process_id = queryResponse.process_id;
    if (!process_id) {
      if (this.DEBUG) console.log('Service - _doOneCheckAnswer : Missing process_id, canot request the API, abort.');
      return new Promise((resolve) => {
        resolve({
          status: "error",
          message: "Missing process_id"
        });
      });
    }

    return new Promise((resolve) => {
      if (this.DEBUG) console.log('Service - _doOneCheckAnswer : api.getCheckAnswer...',{token:this.token,session_id,process_id});
      this.api.getCheckAnswer(this.token, session_id, process_id)
        .then(async (res) => ({
          status: res.status,
          body: res.ok ? await res.json() : {}
        }))
        .then(({ status, body }) => {
          if (this.DEBUG) console.log('Service - _doOneCheckAnswer, response is',{status,body});
          this.checkingAnswer = false;

          // HACK TEST DEV :
          //status = 500;

          // 200 = reponse complete
          // 202 sans message_history = ancien status "delayed"
          // 202 avec message_history = nouveau status "processing"
          // 500 : on reessaye 3 fois
          if (status === 200 || status === 202 /* delayed / processing */) {
            if (this.DEBUG) console.log('Service - _doOneCheckAnswer, seems good',body);
            resolve(body);
          }
          else if (status === 500) {
            if (this.DEBUG) console.log('Service - _doOneCheckAnswer, error 500',body);
            // NB : 12 fev 2024 : probleme rencontré en back => on a systematiquement le premier check_answer
            // en 500.
            // Decision : on le comptabilise, mais on y répond comme un processing...
            // resolve({
            //   status: "error",
            //   message: 500
            // });
            resolve({
              "session_id": this.session_id,
              "process_id": this.process_id,
              "retry_after": this.retry_after !== undefined ? this.retry_after : 2,
              "status": "processing",
              "500": true
            });
          }
          else {
            resolve({
              status: "error",
              message: body.message
            });
          }
        })
        .catch(error => {
          if (this.DEBUG) console.log('Service - _doOneCheckAnswer, cannot get the response',error);
          this.checkingAnswer = false;
          resolve({
            status: "error",
            message: error
          });
        });
    });
  }

  /**
   *
   * @param {string} uservalue should be a float between 1 and 5
   * @returns {Promise} si error, renvoie le message {string}. si ok, renvoie un {object} avec le body de la reponse
   */
  transmitRate(uservalue) {
    if (this.DEBUG) console.log('Service - transmitRate...');

    // check token
    if (!this.token) {
      if (this.DEBUG) console.log('Service - transmitRate : Missing token, cannot request the API, abort.');
      return new Promise((resolve) => {
        resolve({
          status: "error",
          message: "Missing token"
        });
      });
    }

    // check session_id
    // on est sensé en avoir un depuis la 1re query avec réponse valide
    if (!this.session_id) {
      if (this.DEBUG) console.log('Service - transmitRate : Missing session_id, cannot request the API, abort.');
      return new Promise((resolve) => {
        resolve({
          status: "error",
          message: "Missing session_id"
        });
      });
    }

    this.rating = true;

    const bodycontents = {
      rating: uservalue
    };

    return new Promise((resolve) => {
      if (this.DEBUG) console.log('Service - transmitRate : api.postRate...',{token:this.token,session_id:this.session_id,bodycontents});
      this.api.postRate(this.token, this.session_id, bodycontents)
        .then(async (res) => ({
          status: res.status,
          body: await res.json()
        }))
        .then(({ status, body }) => {
          if (this.DEBUG) console.log('Service - transmitRate, response is',{status,body});
          this.querying = false;
          if (status === 200) {
            if (this.DEBUG) console.log('Service - transmitRate, seems good',body);
            this.resetProcess();
            resolve(body);
          }
          else if (status === 409) {  // conflict
            if (this.DEBUG) console.log('Service - transmitRate, conflict',body);
            if (this.process_id === undefined) {
              // on simule une reponse correcte de rate
              const fake200Body = {
                status: API_STATUS.TERMINATED,
              };
              resolve(fake200Body);
            } else {
              // check_answer
              const fake200Body = {
                status: API_STATUS.PROCESSING,
                session_id: this.session_id,
                process_id: this.process_id,
                retry_after: 2, // 2 sec arbitraire
              };
              resolve(fake200Body);
            }
          }
          else {
            resolve({
              status: "error",
              message: body.message
            });
          }
        }).catch(error => {
          if (this.DEBUG) console.log('Service - transmitRate, cannot get the response',error);
          this.rating = false;
          resolve({
            status: "error",
            message: error
          });
        });
    });
  }

  resetProcess() {
    this.rating = false;
    this.process_id = undefined;  // {string} ID lié à toute la conversation et au precess
    if (this.USE_CACHE) localStorage.removeItem(config.CACHE_VAR+'_process_id');
    if (this.DEBUG) console.log('Service - Process reseted.');
  }

  /**
   * Reset only session
   */
  resetSession() {
    this.querying = false;  // true si une requete query est en cours
    this.checkingAnswer = false;  // true si une requete checkAnswer est en cours
    this.rating = false;
    this.session_id = undefined;  // {string} ID lié à toute la conversation à partir du moment où elle est engagée
    if (this.USE_CACHE) localStorage.removeItem(config.CACHE_VAR+'_session_id');
    this.process_id = undefined;  // {string} ID lié à toute la conversation et au precess
    if (this.USE_CACHE) localStorage.removeItem(config.CACHE_VAR+'_process_id');
    // this.messages_history_id = undefined;
    // if (this.USE_CACHE) localStorage.removeItem(config.CACHE_VAR+'_messages_history_id');

    if (this.DEBUG) console.log('Service - Session reseted.');
  }

  /**
   * Clear cache and reset conversation
   */
  reset() {
    this.querying = false;  // true si une requete query est en cours
    this.checkingAnswer = false;  // true si une requete checkAnswer est en cours
    this.rating = false;
    this.session_id = undefined;  // {string} ID lié à toute la conversation à partir du moment où elle est engagée
    if (this.USE_CACHE) localStorage.removeItem(config.CACHE_VAR+'_session_id');
    this.process_id = undefined;  // {string} ID lié à toute la conversation et au precess
    if (this.USE_CACHE) localStorage.removeItem(config.CACHE_VAR+'_process_id');

    if (this.USE_CACHE) localStorage.removeItem(config.CACHE_VAR+'_last_agent_ids');

    // this.messages_history_id = undefined;
    // if (this.USE_CACHE) localStorage.removeItem(config.CACHE_VAR+'_messages_history_id');
    localStorage.removeItem(config.CACHE_VAR);
    if (this.DEBUG) console.log('Service - Reseted.');
  }

}

Service._instance = null;

export default Service;
